import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, Slide, Stack, Typography, Avatar, IconButton, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { PhoneDisconnect } from 'phosphor-react';
import createAvatar from '../utils/createAvatar';
import { useSelector, useDispatch } from 'react-redux';
import { endCall, resetCallTimer, incrementCallTimer } from '../redux/slices/call';
import useSocket from '../hooks/useSocket';
import { PROFILE_IMAGE_URL } from '../config'; // Import PROFILE_IMAGE_URL

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CallWindow = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { endCurrentCall, BASE_URL, CALLING_URL } = useSocket();
  const { callWindowOpen, activeCallDetails, callTimer } = useSelector((state) => state.call);
  const timerIntervalRef = useRef(null);

  const partnerName = activeCallDetails?.callerId === activeCallDetails?.caller_id
    ? activeCallDetails?.receiverName
    : activeCallDetails?.callerName;
  const partnerId = activeCallDetails?.callerId === activeCallDetails?.caller_id
    ? activeCallDetails?.receiverId
    : activeCallDetails?.callerId;
  const type = activeCallDetails?.type || 'audio';
  const roomId = activeCallDetails?.roomId;

  const avatarProps = createAvatar(partnerName);
  const avatarSrc = partnerId
    ? `${PROFILE_IMAGE_URL}${partnerId}` // Use PROFILE_IMAGE_URL
    : undefined;

  const handleHangup = () => {
    endCurrentCall(); // Emit endCall via socket and update Redux state
  };

  // Timer logic
  useEffect(() => {
    if (callWindowOpen) {
      // Reset timer when modal opens
      dispatch(resetCallTimer());
      timerIntervalRef.current = setInterval(() => {
        dispatch(incrementCallTimer());
      }, 1000);
    } else {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [callWindowOpen, dispatch]);

  const [cachedRoomId, setCachedRoomId] = useState(null);
  const [cachedType, setCachedType] = useState('audio');
  const [isIframeActive, setIsIframeActive] = useState(false);

  useEffect(() => {
    if (callWindowOpen && activeCallDetails?.roomId) {
      setCachedRoomId(activeCallDetails.roomId);
      setCachedType(activeCallDetails.type || 'audio');
      setIsIframeActive(true);
    } else if (!callWindowOpen) {
      // Keep iframe active for 2 seconds after modal closes to allow "hangup" requests to finish
      const timer = setTimeout(() => {
        setIsIframeActive(false);
        setCachedRoomId(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [callWindowOpen, activeCallDetails]);

  const formatTime = (h, m, s) => {
    const pad = (num) => num < 10 ? '0' + num : num;
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
  };

  const iframeSrc = cachedRoomId && CALLING_URL
    ? `${CALLING_URL}/r/${cachedRoomId}?video=${cachedType === 'video'}`
    : '';

  return (
    <Dialog
      open={callWindowOpen}
      TransitionComponent={Transition}
      keepMounted // Crucial: keep DOM alive so iframe can finish requests even when Dialog is "hidden"
      onClose={handleHangup}
      aria-describedby="call-window-description"
      fullScreen
      sx={{ zIndex: 1400 }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 0, position: 'relative', zIndex: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
          <Box sx={{ width: 64 }} />
          <Stack alignItems="center">
            <Typography variant="h5" sx={{ color: theme.palette.text.primary }}>
              {cachedType === 'audio' ? 'Audio Call' : 'Video Call'}
            </Typography>
            <Typography variant="h6" sx={{ color: theme.palette.text.secondary }}>
              {partnerName}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              {formatTime(callTimer.hours, callTimer.minutes, callTimer.seconds)}
            </Typography>
          </Stack>
          <Box sx={{ width: 64 }} />
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ p: 0, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {isIframeActive && iframeSrc ? (
          <iframe
            src={iframeSrc}
            title="Video Call"
            allow="camera; microphone; fullscreen"
            style={{ border: 'none', width: '100%', height: '100%' }}
          />
        ) : (
          <Stack alignItems="center" justifyContent="center" sx={{ flexGrow: 1 }}>
            {!isIframeActive && !callWindowOpen ? null : <Typography variant="h6" color="error">Call connection failed.</Typography>}
          </Stack>
        )}
      </DialogContent>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
        <IconButton
          onClick={handleHangup}
          sx={{
            backgroundColor: theme.palette.error.main,
            color: theme.palette.common.white,
            width: 64,
            height: 64,
            '&:hover': {
              backgroundColor: theme.palette.error.dark,
            },
          }}
        >
          <PhoneDisconnect size={32} />
        </IconButton>
      </Box>
    </Dialog>
  );
};

export default CallWindow;